import React, { useState } from 'react';
import { Share2, Facebook, Twitter, Linkedin, Mail, Link2, X, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { propertyData, brandAssets } from '../../data/mock';

const ShareModal = ({ isOpen, onClose }) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const currentUrl = window.location.href;
  const shareTitle = `${propertyData.brandName} - ${propertyData.priceShort}`;
  const shareText = `Discover this private 20-acre Central Florida legacy estate - ${propertyData.priceShort}`;

  const shareLinks = {
    facebook: "https://www.facebook.com/sharer/sharer.php?u=",
    twitter: "https://twitter.com/intent/tweet?url=",
    linkedin: "https://www.linkedin.com/shareArticle?mini=true&url="
  };

  const shareOptions = [
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'hover:bg-blue-600/20 hover:border-blue-500/50 hover:text-blue-400',
      url: `${shareLinks.facebook}${encodeURIComponent(currentUrl)}`,
    },
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'hover:bg-sky-500/20 hover:border-sky-500/50 hover:text-sky-400',
      url: `${shareLinks.twitter}${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(shareText)}`,
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'hover:bg-blue-700/20 hover:border-blue-600/50 hover:text-blue-400',
      url: `${shareLinks.linkedin}${encodeURIComponent(currentUrl)}&title=${encodeURIComponent(shareTitle)}`,
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'hover:bg-amber-500/20 hover:border-amber-500/50 hover:text-amber-400',
      url: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareText}\n\n${currentUrl}`)}`,
    },
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopiedLink(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopiedLink(false), 3000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const emailToSelf = () => {
    const subject = encodeURIComponent(`Save for Later: ${propertyData.brandName}`);
    const body = encodeURIComponent(
      `I found this property and wanted to save it for later:\n\n` +
      `${propertyData.brandName}\n` +
      `${propertyData.brandTagline}\n\n` +
      `Price: ${propertyData.priceShort}\n` +
      `Address: ${propertyData.address}\n\n` +
      `View the full listing: ${currentUrl}\n\n` +
      `For inquiries: ${propertyData.estateContact.email}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="share-modal">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gradient-to-br from-[#1a1f2e] to-[#0d1117] border border-white/10 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors"
          data-testid="share-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <img 
            src={brandAssets.wCrest} 
            alt="William Lane Estate" 
            className="w-16 h-16 mx-auto mb-4 object-contain"
          />
          <h3 className="text-2xl font-semibold text-white mb-2">Share This Estate</h3>
          <p className="text-white/60 text-sm">Let others know about this exceptional property</p>
        </div>

        {/* Share Options */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {shareOptions.map((option) => (
            <a
              key={option.name}
              href={option.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-center space-x-2 p-4 bg-white/5 border border-white/10 rounded-xl text-white/70 transition-all duration-300 ${option.color}`}
              data-testid={`share-${option.name.toLowerCase()}`}
            >
              <option.icon className="w-5 h-5" />
              <span className="text-sm font-medium">{option.name}</span>
            </a>
          ))}
        </div>

        {/* Copy Link */}
        <div className="mb-4">
          <p className="text-white/60 text-sm mb-2">Or copy the link</p>
          <div className="flex items-center space-x-2">
            <div className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white/60 text-sm truncate">
              {currentUrl}
            </div>
            <button
              onClick={copyToClipboard}
              className={`px-4 py-3 rounded-lg flex items-center justify-center transition-all duration-300 ${
                copiedLink
                  ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                  : 'bg-amber-500/20 border border-amber-500/50 text-amber-400 hover:bg-amber-500/30'
              }`}
              data-testid="copy-link-btn"
            >
              {copiedLink ? <CheckCircle className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Email to Self */}
        <Button
          onClick={emailToSelf}
          variant="outline"
          className="w-full border-white/20 text-white hover:bg-white/10 hover:border-amber-500/50"
          data-testid="email-self-btn"
        >
          <Mail className="w-4 h-4 mr-2" />
          Email This Property to Myself
        </Button>
      </div>
    </div>
  );
};

export default ShareModal;
